import { genUploader } from 'uploadthing/client';

import type { DriveAuthSession } from '../types';

export interface UploadThingStoredFile {
  key: string;
  name: string;
  size: number;
  uploadedAt: string;
}

export interface UploadThingListResponse {
  files: UploadThingStoredFile[];
  totalUploads: number;
  remainingUploads: number;
  maxUploads: number;
}

const API_BASE = '/api/uploadthing';

const authHeaders = (session: DriveAuthSession): HeadersInit => ({
  Authorization: `Bearer ${session.idToken || session.accessToken}`,
  'x-access-token': session.accessToken,
});

const uploader = genUploader<any>({ url: API_BASE });

const parseResponseError = async (response: Response): Promise<string> => {
  try {
    const data = await response.json();
    return data?.error || data?.message || 'Upload request failed.';
  } catch {
    return 'Upload request failed.';
  }
};

export const uploadthingService = {
  async list(session: DriveAuthSession): Promise<UploadThingListResponse> {
    const response = await fetch(API_BASE, {
      method: 'GET',
      headers: authHeaders(session),
    });

    if (!response.ok) {
      throw new Error(await parseResponseError(response));
    }

    return await response.json();
  },

  async uploadFiles(session: DriveAuthSession, files: File[]): Promise<UploadThingListResponse> {
    if (files.length === 0) {
      return this.list(session);
    }

    for (const file of files) {
      await this.uploadFile(session, file);
    }

    return this.list(session);
  },

  async uploadFile(session: DriveAuthSession, file: File): Promise<void> {
    await uploader.uploadFiles('mediaUploader', {
      files: [file],
      headers: authHeaders(session),
    });
  },

  // Stored files are private; bytes are only reachable through a short-lived
  // signed URL the server issues after confirming the caller owns the key.
  async getSignedUrl(accessToken: string, key: string): Promise<string> {
    const response = await fetch(
      `${API_BASE}?action=signed-url&key=${encodeURIComponent(key)}`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'x-access-token': accessToken,
        },
      },
    );

    if (!response.ok) {
      throw new Error(await parseResponseError(response));
    }

    const payload = await response.json();
    const url = typeof payload?.url === 'string' ? payload.url : '';
    if (!url) throw new Error('Signed URL missing from response.');
    return url;
  },
};
